import { useEffect, useState } from "react";
import { CheckCircle2, CircleDashed, DownloadCloud, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";
import { Card, LoadingBlock, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function GoogleTab() {
  const [g, setG] = useState(null);
  const [json, setJson] = useState("");
  const [sheets, setSheets] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/google").then((r) => { setG(r.data); setSheets((s) => s || Object.fromEntries(r.data.brand_sheets.map((b) => [b.brand, b.id]))); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (!g?.pushing) return undefined;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g?.pushing]);
  const act = async (name, fn) => {
    setBusy(name);
    try {
      await fn();
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  if (!g || !sheets) return <LoadingBlock />;
  const linked = g.brand_sheets.filter((b) => b.id);
  return (
    <div className="space-y-6" data-testid="google-tab">
      <Card testid="google-status-card">
        <div className="flex flex-wrap items-center gap-4">
          {g.connected ? <CheckCircle2 className="h-10 w-10 text-emerald-500" /> : <CircleDashed className="h-10 w-10 text-slate-300" />}
          <div className="flex-1">
            <div className="text-lg font-semibold" data-testid="google-connection-status">{g.connected ? `Connected to ${linked.length} brand sheet${linked.length > 1 ? "s" : ""}` : "Not connected yet"}</div>
            {g.connected && <ul className="mt-1 text-sm text-slate-600" data-testid="google-brand-sheet-list">{linked.map((b) => <li key={b.brand}><b>{b.brand}</b> → "{b.title}" (one tab per month + "GRM Closed")</li>)}</ul>}
            <div className="text-sm text-slate-500">
              {g.connected ? `Last write: ${fmtDateTime(g.last_push_at)} · ${g.last_push_message || ""}` : "The app works fully without Google. Records are kept here and written to the sheet once you connect."}
            </div>
            <div className="mt-1 text-sm text-slate-500" data-testid="google-pending-rows">{g.pushing && <Spinner className="mr-1 inline h-3.5 w-3.5" />}{g.pending_rows} records waiting to be written to the brand sheets{g.pushing ? " · writing now…" : ""}</div>
            {g.connected && g.last_push_status === "error" && !g.pushing && <div className="mt-1 text-sm text-red-600" data-testid="google-push-error">{g.last_push_message}</div>}
            {g.connected && (
              <div className={`mt-1 text-sm ${g.last_pull_status === "error" ? "text-red-600" : "text-slate-500"}`} data-testid="google-last-pull">
                Sheet edits (Category, Severity) last checked: {fmtDateTime(g.last_pull_at)}{g.last_pull_message ? ` · ${g.last_pull_message}` : ""}
              </div>
            )}
          </div>
          {g.connected && (
            <div className="flex gap-2">
              <Button className="rounded-xl" onClick={() => act("push", async () => toast.success((await api.post("/google/push")).data.message))} disabled={!!busy || g.pushing} data-testid="google-push-button">{busy === "push" || g.pushing ? <Spinner /> : <UploadCloud className="mr-2 h-4 w-4" />}{g.pushing ? "Writing…" : "Write now"}</Button>
              <Button variant="outline" className="rounded-xl" onClick={() => act("pull", async () => toast.success((await api.post("/google/pull")).data.message))} disabled={!!busy} data-testid="google-pull-button">{busy === "pull" ? <Spinner /> : <DownloadCloud className="mr-2 h-4 w-4" />}Pull sheet edits</Button>
              <Button variant="outline" className="rounded-xl" onClick={() => act("dis", () => api.post("/google/disconnect"))} data-testid="google-disconnect-button">Disconnect</Button>
            </div>
          )}
        </div>
      </Card>
      <Card title={g.connected ? "Change connection" : "Connect Google Sheets"} subtitle="See the Help tab for step-by-step setup instructions." testid="google-connect-card">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Service account key (JSON)</Label>
            <Textarea data-testid="google-json-input" rows={6} className="rounded-xl font-mono text-xs" placeholder={g.has_key ? `Key saved for ${g.service_account_email}. Paste a new one only to replace it.` : '{ "type": "service_account", "project_id": "...", ... }'} value={json} onChange={(e) => setJson(e.target.value)} />
            {g.service_account_email && <p className="text-sm text-slate-600">Share your sheets with: <b className="select-all" data-testid="service-account-email">{g.service_account_email}</b></p>}
          </div>
          {g.brand_sheets.map(({ brand }) => (
            <div className="space-y-2" key={brand}>
              <Label>{brand} sheet link</Label>
              <Input data-testid={`google-sheet-input-${brand.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="h-11 rounded-xl" placeholder="https://docs.google.com/spreadsheets/d/..." value={sheets[brand] || ""} onChange={(e) => setSheets({ ...sheets, [brand]: e.target.value })} />
            </div>
          ))}
          <Button className="h-11 rounded-xl" disabled={!!busy} data-testid="google-connect-button"
            onClick={() => act("save", async () => { const r = await api.put("/google", { service_account_json: json || null, sheets }); toast.success(`Connected ${Object.keys(r.data.sheets).length} brand sheet(s)`); setJson(""); })}>
            {busy === "save" ? <Spinner /> : "Test & save connection"}
          </Button>
        </div>
      </Card>
      {g.sample_live > 0 && <Card title="Sample data" subtitle="The app came with sample records for 3 brands so you can try it. Archive them before going live (they are hidden, not deleted, and never written to your sheet unless edited).">
        <Button variant="outline" className="rounded-xl" data-testid="archive-sample-data-button"
          onClick={() => window.confirm("Archive all sample records?") && act("sample", async () => toast.success(`${(await api.post("/sample-data/archive")).data.archived} sample records archived`))}>
          Archive sample data
        </Button>
      </Card>}
    </div>
  );
}
